import { FC, useMemo, useState } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { ScalarLineChart } from 'components/Charts';
import {
  useGetCompanyZanbatoAskPricing,
  useGetCompanyZanbatoBidPricing,
  useGetCompanyZanbatoClosedTransactions,
  useGetCompanyZanbatoPricing,
} from 'services/hooks/companies';
import { CardBody, CardTitle, CardTitleRow, OutlinedCard, SourceChip } from '../CompanyOverview.styles';
import {
  ChangeLabel,
  CurrentPrice,
  HeaderLeft,
  PeriodControls,
  RobustnessRow,
  SeriesToggleRow,
  StatBar,
  StatItem,
  StatLabel,
  StatValue,
  Subtitle,
} from './ZXIndexValueCard.styles';
import {
  buildAllSeries,
  buildTooltipExtras,
  buildTooltipFormatter,
  computeHeaderStats,
  DEFAULT_ENABLED_IDS,
  filterByPeriod,
  formatCurrency,
  formatCurrencyStr,
  formatPricingDate,
  formatVolume,
  getAsOfRecord,
  getPeriodStartDate,
  getRobustnessInfo,
  Period,
  PERIODS,
  PeriodValue,
  ROBUSTNESS_METHODOLOGY,
  ROBUSTNESS_SCORE_RANGES,
  SERIES_CONFIGS,
} from './ZXIndexValueCard.utils';
import { EndDateMode, EndDateModeValue } from '../MutualFundMarksCard/MutualFundMarksCard.utils';

interface Props {
  companyId: number | undefined;
  measurementDate?: string;
  endDateMode: EndDateModeValue;
}

const ZXIndexValueCard: FC<Props> = ({ companyId, measurementDate, endDateMode }) => {
  const [period, setPeriod] = useState<PeriodValue>(Period.Y1);
  const [enabledIds, setEnabledIds] = useState<Set<string>>(DEFAULT_ENABLED_IDS);
  const [robustnessModalOpen, setRobustnessModalOpen] = useState(false);

  const { data: pricingData, isLoading: pricingLoading } = useGetCompanyZanbatoPricing(companyId);
  const { data: askData, isLoading: askLoading } = useGetCompanyZanbatoAskPricing(companyId);
  const { data: bidData, isLoading: bidLoading } = useGetCompanyZanbatoBidPricing(companyId);
  const { data: txData, isLoading: txLoading } = useGetCompanyZanbatoClosedTransactions(companyId);

  const isLoading = pricingLoading || askLoading || bidLoading || txLoading;

  const allPricing = useMemo(() => pricingData?.pricing ?? [], [pricingData?.pricing]);
  const allAsk = useMemo(() => askData?.pricing ?? [], [askData?.pricing]);
  const allBid = useMemo(() => bidData?.pricing ?? [], [bidData?.pricing]);
  const allTransactions = useMemo(() => txData?.transactions ?? [], [txData?.transactions]);

  const latestDate = useMemo(() => {
    if (!allPricing.length) {
      return undefined;
    }
    return (
      [...allPricing].filter(i => i.report_period).sort((a, b) => b.report_period!.localeCompare(a.report_period!))[0]
        ?.report_period ?? undefined
    );
  }, [allPricing]);

  const effectiveEndDate = endDateMode === EndDateMode.Measurement && measurementDate ? measurementDate : latestDate;

  const filteredPricing = useMemo(() => {
    if (!effectiveEndDate || !allPricing.length) {
      return allPricing;
    }
    return filterByPeriod(allPricing, period, effectiveEndDate);
  }, [allPricing, period, effectiveEndDate]);

  const filteredAsk = useMemo(() => {
    if (!effectiveEndDate || !allAsk.length) {
      return allAsk;
    }
    return filterByPeriod(allAsk, period, effectiveEndDate);
  }, [allAsk, period, effectiveEndDate]);

  const filteredBid = useMemo(() => {
    if (!effectiveEndDate || !allBid.length) {
      return allBid;
    }
    return filterByPeriod(allBid, period, effectiveEndDate);
  }, [allBid, period, effectiveEndDate]);

  // Transactions use closing_date rather than report_period so filter inline
  const filteredTransactions = useMemo(() => {
    if (!allTransactions.length) {
      return allTransactions;
    }
    if (period === Period.All) {
      return allTransactions.filter(t => t.closing_date);
    }
    if (!effectiveEndDate) {
      return allTransactions;
    }
    const startDate = getPeriodStartDate(effectiveEndDate, period);
    return allTransactions.filter(t => {
      if (!t.closing_date) {
        return false;
      }
      if (t.closing_date > effectiveEndDate) {
        return false;
      }
      if (startDate && t.closing_date < startDate) {
        return false;
      }
      return true;
    });
  }, [allTransactions, period, effectiveEndDate]);

  const series = useMemo(
    () => buildAllSeries(enabledIds, filteredPricing, filteredAsk, filteredBid, filteredTransactions),
    [enabledIds, filteredPricing, filteredAsk, filteredBid, filteredTransactions]
  );

  const tooltipFormatter = useMemo(() => {
    const extras = buildTooltipExtras(allPricing, allAsk, allBid, allTransactions);
    return buildTooltipFormatter(extras);
  }, [allPricing, allAsk, allBid, allTransactions]);

  const asOfRecord = useMemo(() => getAsOfRecord(allPricing, effectiveEndDate), [allPricing, effectiveEndDate]);

  const { currentValue, change } = useMemo(
    () => computeHeaderStats(filteredPricing, effectiveEndDate),
    [filteredPricing, effectiveEndDate]
  );

  const toggleSeries = (id: string) => {
    setEnabledIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const robustness = getRobustnessInfo(asOfRecord?.robustness);
  const hasData = allPricing.length > 0 || allAsk.length > 0 || allBid.length > 0 || allTransactions.length > 0;

  return (
    <OutlinedCard variant="outlined">
      <CardBody>
        <CardTitleRow>
          <HeaderLeft>
            <CardTitle component="span">ZX Index Value</CardTitle>
            {asOfRecord?.zx_index_value != null && (
              <Tooltip
                arrow
                title={
                  <div>
                    <div style={{ fontWeight: 700, marginBottom: 2, fontSize: '0.8rem' }}>ZX Index Value (Zanbato)</div>
                    <div style={{ fontSize: '0.78rem' }}>{formatCurrencyStr(asOfRecord.zx_index_value)}</div>
                    {asOfRecord.report_period && (
                      <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.65)', marginTop: 4 }}>
                        As of {formatPricingDate(asOfRecord.report_period)}
                      </div>
                    )}
                  </div>
                }>
                <InfoOutlinedIcon sx={{ fontSize: '0.9rem', color: 'text.disabled', cursor: 'default', ml: 0.5 }} />
              </Tooltip>
            )}
            {hasData && (
              <>
                <CurrentPrice>{formatCurrency(currentValue)}</CurrentPrice>
                {change && (
                  <ChangeLabel positive={change.abs >= 0}>
                    {change.abs >= 0 ? '+' : ''}
                    {change.abs.toFixed(2)} ({change.abs >= 0 ? '+' : ''}
                    {change.pct.toFixed(2)}%)
                  </ChangeLabel>
                )}
              </>
            )}
          </HeaderLeft>

          {hasData && (
            <PeriodControls>
              <ToggleButtonGroup
                value={period}
                exclusive
                size="small"
                onChange={(_e, v: PeriodValue | null) => {
                  if (v) {
                    setPeriod(v);
                  }
                }}>
                {PERIODS.map(p => (
                  <ToggleButton key={p} value={p}>
                    {p}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.7rem !important' }} />}
                href="https://zanbato.com"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontSize: '0.72rem', fontWeight: 600, textTransform: 'none', height: 26, py: 0 }}>
                Zanbato
              </Button>
            </PeriodControls>
          )}

          {!hasData && <SourceChip label="Zanbato" size="small" chipColor="blue" />}
        </CardTitleRow>

        {hasData && (
          <Subtitle>
            <span>ZX Index Value (Zanbato){effectiveEndDate ? ` as of ${effectiveEndDate}` : ''}</span>
            {robustness && (
              <>
                <Chip
                  label={robustness.label}
                  size="small"
                  color={robustness.color}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                />
                <Tooltip
                  arrow
                  title={
                    <div style={{ maxWidth: 280 }}>
                      <div style={{ fontWeight: 700, marginBottom: 4, fontSize: '0.8rem' }}>
                        {robustness.label}: {robustness.confidence}
                      </div>
                      <div style={{ fontSize: '0.75rem', lineHeight: 1.55, marginBottom: 8 }}>
                        {robustness.scoreDescription}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.65)', fontStyle: 'italic' }}>
                        Click to see all score ranges
                      </div>
                    </div>
                  }>
                  <InfoOutlinedIcon
                    onClick={() => setRobustnessModalOpen(true)}
                    sx={{ fontSize: '0.85rem', color: 'text.disabled', cursor: 'pointer' }}
                  />
                </Tooltip>
              </>
            )}
          </Subtitle>
        )}

        {isLoading && <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />}

        {!isLoading && !hasData && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 3 }}>
            No Zanbato pricing data available for this company.
          </Typography>
        )}

        {!isLoading && hasData && (
          <>
            <SeriesToggleRow>
              {SERIES_CONFIGS.map(s => (
                <Chip
                  key={s.id}
                  label={s.label}
                  size="small"
                  onClick={() => toggleSeries(s.id)}
                  variant={enabledIds.has(s.id) ? 'filled' : 'outlined'}
                  sx={{
                    fontSize: '0.72rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    backgroundColor: enabledIds.has(s.id) ? s.color : 'transparent',
                    borderColor: s.color,
                    color: enabledIds.has(s.id) ? '#fff' : s.color,
                    '&:hover': {
                      backgroundColor: enabledIds.has(s.id) ? s.color : `${s.color}22`,
                    },
                  }}
                />
              ))}
            </SeriesToggleRow>

            <ScalarLineChart
              series={series}
              notMerge
              smooth
              showPoints={false}
              style={{ height: 280 }}
              config={{
                legend: { show: false },
                xAxis: { formatter: v => formatPricingDate(String(v)) },
                yAxis: { formatter: v => `$${v}` },
                options: {
                  tooltip: {
                    trigger: 'axis',
                    appendToBody: true,
                    formatter: tooltipFormatter,
                  },
                },
              }}
            />

            <StatBar>
              <StatItem>
                <StatLabel>ZX Index Value (Current)</StatLabel>
                <StatValue>{formatCurrency(currentValue)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Volume</StatLabel>
                <StatValue>{formatVolume(asOfRecord?.total_volume)}</StatValue>
              </StatItem>
              <StatItem>
                <StatLabel>Robustness Score</StatLabel>
                <StatValue>
                  {robustness ? (
                    <RobustnessRow>
                      <Chip
                        label={robustness.label}
                        size="small"
                        color={robustness.color}
                        sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                      />
                      <span style={{ fontSize: '0.82rem', fontWeight: 400 }}>{robustness.confidence}</span>
                    </RobustnessRow>
                  ) : (
                    '—'
                  )}
                </StatValue>
              </StatItem>
            </StatBar>
          </>
        )}
      </CardBody>
      <Dialog open={robustnessModalOpen} onClose={() => setRobustnessModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1rem', fontWeight: 700 }}>ZXData Robustness Score</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ lineHeight: 1.7, mb: 3 }}>
            {ROBUSTNESS_METHODOLOGY}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'text.secondary',
              display: 'block',
              mb: 1.5,
            }}>
            Score Interpretations
          </Typography>
          {ROBUSTNESS_SCORE_RANGES.map(range => (
            <div key={range.label} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14 }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 3,
                  minWidth: 68,
                  paddingTop: 2,
                }}>
                <Chip
                  label={`RS${range.label}`}
                  size="small"
                  color={range.color}
                  sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, borderRadius: 1 }}
                />
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.65rem', color: 'text.secondary', textAlign: 'center', lineHeight: 1.3 }}>
                  {range.confidence}
                </Typography>
              </div>
              <Typography variant="body2" sx={{ lineHeight: 1.65, fontSize: '0.82rem', pt: 0.25 }}>
                {range.description}
              </Typography>
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={() => setRobustnessModalOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </OutlinedCard>
  );
};

export { ZXIndexValueCard };
